import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { API_ENDPOINTS, buildApiUrl } from '../config/apiConfig';
import SapResult from './SapResult';
import ChatMarkdown from './ChatMarkdown';
import { parseMcpResult, blocksToPlainText } from '../utils/mcpFormat';
import './SapAssistant.css';

/**
 * SapAssistant
 * ============
 * Dedicated SAP feature (SAP users only), presented as a CHATBOT. Two modes,
 * chosen by the backend config (`chatEnabled`):
 *
 *   1. Natural-language chat (chatEnabled=true): the user just types a question.
 *      AWS Bedrock (Claude) decides which SAP MCP tool(s) to call (tool-use),
 *      the backend runs them via the XSUAA proxy, and Bedrock writes the answer.
 *      This is ASYNC (submit -> jobId -> poll) because the tool-use loop can
 *      exceed API Gateway's 29s - exactly like KoneChat.
 *
 *   2. Tool-picker fallback (chatEnabled=false): when Bedrock is unavailable the
 *      user picks an MCP tool and types a question; the backend maps it to the
 *      tool's arguments deterministically (/api/sap/ask) and runs tools/call.
 *
 * Assistant answers render Markdown (ChatMarkdown) plus any structured SAP data
 * as rich UI (SapResult: sortable tables, key/value grids, JSON trees).
 *
 * Requests flow through the shared axios instance so the MSAL bearer token is
 * attached automatically (setupAxiosAuth.js). SAP-only access is enforced
 * server-side; this component is only mounted for SAP users / admins.
 */

let msgSeq = 0;
const nextId = () => `m${Date.now()}_${msgSeq++}`;
const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// Persist the conversation across route changes (survives leaving/returning to
// the SAP page within the session; cleared when the browser tab closes). We use
// sessionStorage so SAP data isn't retained longer than the session.
const STORE_KEY = 'icop.sap.chat.v1';

const loadStoredMessages = () => {
  try {
    const raw = sessionStorage.getItem(STORE_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    return Array.isArray(parsed) ? parsed : [];
  } catch (e) {
    return [];
  }
};

const saveStoredMessages = (messages) => {
  try {
    sessionStorage.setItem(STORE_KEY, JSON.stringify(messages || []));
  } catch (e) {
    // Storage can be full/blocked; persistence is best-effort.
  }
};

// Poll settings for the async chat result (matches KoneChat's cadence).
const POLL_INTERVAL_MS = 2000;
const POLL_MAX_ATTEMPTS = 150; // ~5 minutes

// Compact "field: value" summary of the arguments the backend derived (fallback
// mode) or the tools Bedrock used (chat mode), shown under the answer.
const summarise = (obj) => {
  const entries = Object.entries(obj || {}).filter(
    ([, v]) => v !== '' && v !== null && v !== undefined
  );
  if (entries.length === 0) return '';
  return entries.map(([k, v]) => `${k}: ${typeof v === 'string' ? v : JSON.stringify(v)}`).join(', ');
};

const summariseToolsUsed = (toolsUsed) => {
  if (!Array.isArray(toolsUsed) || toolsUsed.length === 0) return '';
  return toolsUsed.map((t) => t.name).filter(Boolean).join(' → ');
};

const SapAssistant = () => {
  const [config, setConfig] = useState({ enabled: false, chatEnabled: false, loading: true });
  const [tools, setTools] = useState([]);
  const [toolsLoading, setToolsLoading] = useState(true);
  const [error, setError] = useState(null);

  const [selectedTool, setSelectedTool] = useState('');
  const [question, setQuestion] = useState('');
  const [running, setRunning] = useState(false);

  // Conversation thread (restored from sessionStorage so it survives leaving
  // and returning to the SAP page). Items:
  //   { id, role:'user',      text, tool? }
  //   { id, role:'assistant', answer?, blocks?, metaSummary? }
  //   { id, role:'error',     text }
  const [messages, setMessages] = useState(loadStoredMessages);
  const [copiedId, setCopiedId] = useState(null);

  // Persist the conversation whenever it changes.
  useEffect(() => { saveStoredMessages(messages); }, [messages]);

  const scrollRef = useRef(null);
  const mountedRef = useRef(true);
  useEffect(() => () => { mountedRef.current = false; }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, running]);

  // Load feature config + (for fallback mode) the MCP tool catalog.
  const loadConfig = useCallback(async () => {
    setToolsLoading(true);
    setError(null);
    try {
      const cfgRes = await axios.get(buildApiUrl(API_ENDPOINTS.sapConfig));
      const enabled = !!cfgRes.data?.enabled;
      const chatEnabled = !!cfgRes.data?.chatEnabled;
      setConfig({ enabled, chatEnabled, loading: false, mcpHost: cfgRes.data?.mcpHost });
      if (!enabled) {
        setTools([]);
        return;
      }
      // Only the tool-picker fallback needs the tool catalogue up front.
      if (!chatEnabled) {
        const res = await axios.get(buildApiUrl(API_ENDPOINTS.sapTools));
        const body = res.data || {};
        if (body.error) {
          setError(body.error.message || 'Could not load SAP tools.');
          setTools([]);
        } else {
          const list = body.result?.tools || body.tools || [];
          setTools(Array.isArray(list) ? list : []);
        }
      }
    } catch (err) {
      setConfig((c) => ({ ...c, loading: false }));
      setError(err.response?.data?.message || err.message || 'Failed to load the SAP assistant.');
      setTools([]);
    } finally {
      setToolsLoading(false);
    }
  }, []);

  useEffect(() => { loadConfig(); }, [loadConfig]);

  const currentTool = useMemo(
    () => tools.find((t) => t.name === selectedTool) || null,
    [tools, selectedTool]
  );

  // --- Natural-language chat (async submit + poll) --------------------------
  const askChat = useCallback(async (q) => {
    try {
      const submit = await axios.post(buildApiUrl(API_ENDPOINTS.sapChat), { question: q });
      const jobId = submit.data?.jobId;
      if (!jobId) {
        setMessages((prev) => [...prev, { id: nextId(), role: 'error', text: 'The SAP assistant did not start the request. Please try again.' }]);
        return;
      }
      for (let i = 0; i < POLL_MAX_ATTEMPTS; i += 1) {
        await sleep(POLL_INTERVAL_MS);
        if (!mountedRef.current) return;
        let res;
        try {
          res = await axios.get(buildApiUrl(API_ENDPOINTS.sapChatResult(jobId)));
        } catch (e) {
          if (e?.response?.status === 404) {
            setMessages((prev) => [...prev, { id: nextId(), role: 'error', text: 'The SAP assistant lost track of this request. Please try again.' }]);
            return;
          }
          continue; // transient; keep polling
        }
        const status = res.data?.status;
        if (status === 'done') {
          // Show ONLY the assistant's natural-language answer. The model already
          // summarises the SAP data in its reply, so we do not append the raw
          // MCP payloads as extra blocks.
          const answer = (res.data?.answer || '').trim();
          const metaSummary = summariseToolsUsed(res.data?.toolsUsed);
          setMessages((prev) => [...prev, { id: nextId(), role: 'assistant', answer, metaSummary }]);
          return;
        }
        if (status === 'error') {
          setMessages((prev) => [...prev, { id: nextId(), role: 'error', text: res.data?.error || 'The SAP assistant could not complete the request.' }]);
          return;
        }
        // pending -> keep polling
      }
      setMessages((prev) => [...prev, { id: nextId(), role: 'error', text: 'The SAP assistant is taking longer than expected. Please try again.' }]);
    } catch (err) {
      const detail = err.response?.data?.message || err.message || 'Could not reach the SAP assistant.';
      setMessages((prev) => [...prev, { id: nextId(), role: 'error', text: detail }]);
    }
  }, []);

  // --- Tool-picker fallback (deterministic, /api/sap/ask) -------------------
  const askTool = useCallback(async (q) => {
    try {
      const res = await axios.post(buildApiUrl(API_ENDPOINTS.sapAsk), { tool: selectedTool, question: q });
      const body = res.data || {};
      if (!mountedRef.current) return;
      if (body.error) {
        const detail = body.error.message || body.error.detail || 'The SAP server returned an error.';
        setMessages((prev) => [...prev, { id: nextId(), role: 'error', text: detail }]);
      } else {
        const blocks = parseMcpResult(body.result);
        const metaSummary = summarise(body.arguments);
        setMessages((prev) => [...prev, { id: nextId(), role: 'assistant', blocks, metaSummary }]);
      }
    } catch (err) {
      const detail = err.response?.data?.message || err.message || 'The SAP request failed.';
      setMessages((prev) => [...prev, { id: nextId(), role: 'error', text: detail }]);
    }
  }, [selectedTool]);

  const send = useCallback(async () => {
    const q = question.trim();
    if (!q || running) return;
    // Fallback mode requires a tool selection.
    if (!config.chatEnabled && !selectedTool) return;

    setMessages((prev) => [
      ...prev,
      { id: nextId(), role: 'user', text: q, tool: config.chatEnabled ? undefined : selectedTool },
    ]);
    setQuestion('');
    setRunning(true);
    try {
      if (config.chatEnabled) {
        await askChat(q);
      } else {
        await askTool(q);
      }
    } finally {
      if (mountedRef.current) setRunning(false);
    }
  }, [question, running, config.chatEnabled, selectedTool, askChat, askTool]);

  const clearChat = useCallback(() => setMessages([]), []);

  const copyMessage = useCallback(async (m) => {
    const parts = [];
    if (m.answer) parts.push(m.answer);
    if (m.blocks && m.blocks.length) parts.push(blocksToPlainText(m.blocks));
    const text = parts.join('\n\n') || m.text || '';
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(m.id);
      setTimeout(() => mountedRef.current && setCopiedId(null), 1500);
    } catch (e) {
      // Clipboard can be blocked; ignore silently.
    }
  }, []);

  const chatMode = config.chatEnabled;
  const canSend = !running && question.trim() && (chatMode || selectedTool);

  return (
    <div className="sap-chat">
      <div className="sap-header">
        <div className="sap-title">
          <span className="sap-logo" aria-hidden="true">🧩</span>
          <div>
            <h2>SAP Assistant</h2>
            <p className="sap-subtitle">
              {chatMode ? 'Ask about SAP data in plain language' : 'Query SAP data through KONE\u2019s SAP MCP server'}
              {config.mcpHost ? ` · ${config.mcpHost}` : ''}
            </p>
          </div>
        </div>
        <div className="sap-header-actions">
          {messages.length > 0 && (
            <button type="button" className="sap-ghost-btn" onClick={clearChat} disabled={running}>
              🧹 New chat
            </button>
          )}
          <button type="button" className="sap-ghost-btn" onClick={loadConfig} disabled={toolsLoading}>
            🔄 Refresh
          </button>
        </div>
      </div>

      {!config.loading && !config.enabled && (
        <div className="sap-banner sap-banner--warn">
          SAP is not configured yet. The SAP MCP server URL and XSUAA credentials
          need to be set by the iCOP platform team.
        </div>
      )}

      {error && <div className="sap-banner sap-banner--error">⚠️ {error}</div>}

      {config.enabled && (
        <>
          <div className="sap-thread" ref={scrollRef}>
            {messages.length === 0 && (
              <div className="sap-empty">
                <div className="sap-empty-icon">🧩</div>
                <h3>Ask SAP</h3>
                <p>
                  {chatMode ? (
                    <>Ask a question about SAP data in plain language. The assistant
                    figures out which SAP services to query and answers here.</>
                  ) : (
                    <>Pick a SAP tool below, then type your question. iCOP turns it into
                    the right query and runs it against KONE&apos;s SAP MCP server.</>
                  )}
                </p>
              </div>
            )}

            {messages.map((m) => (
              <div key={m.id} className={`sap-msg sap-msg--${m.role}`}>
                <div className="sap-avatar" aria-hidden="true">
                  {m.role === 'user' ? '🧑' : m.role === 'error' ? '⚠️' : '🧩'}
                </div>
                <div className={`sap-bubble ${m.role === 'error' ? 'sap-bubble--error' : ''} ${m.role === 'assistant' ? 'sap-bubble--wide' : ''}`}>
                  {m.role === 'user' && (
                    <div className="sap-user-turn">
                      {m.tool && <span className="sap-tool-chip">{m.tool}</span>}
                      <span className="sap-question">{m.text}</span>
                    </div>
                  )}
                  {m.role === 'error' && <span>⚠️ {m.text}</span>}
                  {m.role === 'assistant' && (
                    <>
                      <div className="sap-bubble-actions">
                        {m.metaSummary && (
                          <span className="sap-arg-summary" title="SAP tools / query used">
                            🔎 {m.metaSummary}
                          </span>
                        )}
                        <button
                          type="button"
                          className="sap-copy-btn"
                          onClick={() => copyMessage(m)}
                          title="Copy result"
                        >
                          {copiedId === m.id ? '✓ Copied' : '⧉ Copy'}
                        </button>
                      </div>
                      {m.answer && <ChatMarkdown text={m.answer} />}
                      {m.blocks && m.blocks.length > 0 && <SapResult blocks={m.blocks} />}
                    </>
                  )}
                </div>
              </div>
            ))}

            {running && (
              <div className="sap-msg sap-msg--assistant">
                <div className="sap-avatar" aria-hidden="true">🧩</div>
                <div className="sap-bubble sap-typing">
                  <span></span><span></span><span></span>
                </div>
              </div>
            )}
          </div>

          <div className="sap-composer">
            {/* Tool selector only in fallback (no-Bedrock) mode. */}
            {!chatMode && (
              <>
                <div className="sap-tool-row">
                  <span className="sap-field-label">SAP tool</span>
                  <select
                    className="sap-select"
                    value={selectedTool}
                    onChange={(e) => setSelectedTool(e.target.value)}
                    disabled={toolsLoading || running}
                  >
                    <option value="">
                      {toolsLoading ? 'Loading tools…' : `Select a tool (${tools.length} available)`}
                    </option>
                    {tools.map((t) => (
                      <option key={t.name} value={t.name}>{t.name}</option>
                    ))}
                  </select>
                </div>
                {currentTool && currentTool.description && (
                  <p className="sap-tool-desc">{currentTool.description}</p>
                )}
              </>
            )}

            <div className="sap-input-row">
              <textarea
                className="sap-ask-input"
                placeholder={
                  chatMode
                    ? 'Ask about SAP data…  (Enter to send, Shift+Enter for a new line)'
                    : selectedTool
                    ? 'Ask a question…  (Enter to send, Shift+Enter for a new line)'
                    : 'Select a SAP tool first, then ask your question'
                }
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    send();
                  }
                }}
                rows={2}
                disabled={running || (!chatMode && !selectedTool)}
              />
              <button
                type="button"
                className="sap-send"
                onClick={send}
                disabled={!canSend}
              >
                {running ? 'Asking…' : '➤ Ask'}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default SapAssistant;
